import { useQuery } from "@tanstack/react-query";
import { AlertCircle, RefreshCw, TrendingUp } from "lucide-react";
import React, { useMemo, useState } from "react";
import { getDashboardLayout } from "@/api/services/dashboardLayoutService";
import {
	loadLayoutForRole,
	saveLayoutForRole,
} from "@/components/admin/AdminDashboardLayout";
import * as Cards from "@/components/dashboard/cards";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MASTER_DASHBOARD_CARDS } from "@/config/dashboardLayouts";
import { useApp } from "@/contexts/AppContext";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import type { OverdueInvoiceData } from "@/hooks/useDrillDown";
import { usePermissions } from "@/hooks/usePermissions";
import {
	addAssistantMessage,
	addUserMessage,
	type ConversationState,
	createEmptyConversation,
	resetConversation,
} from "@/types/conversation";
import { QueryResultsView } from "./QueryResultsView";

interface DynamicDashboardProps {
	onNavigate?: (
		page: string,
		data?: { accountFilter?: string; highlightSourceType?: string },
	) => void;
}

type ViewMode = "dashboard" | "results";

export function DynamicDashboard({ onNavigate }: DynamicDashboardProps) {
	const {
		currentUser,
		hasPermission,
		isLoading: permissionsLoading,
		roleId,
	} = usePermissions();
	const { mode } = useApp();

	// Live mode: fetch saved layout from API using numeric roleId
	const numericRoleId =
		mode === "live" && typeof roleId === "number" ? roleId : undefined;
	const { data: liveLayoutData } = useQuery({
		queryKey: ["dashboard-layout", numericRoleId],
		queryFn: () => getDashboardLayout(numericRoleId as number),
		enabled: mode === "live" && !!numericRoleId,
	});
	const { metrics, isLoading, error, forecastNotReady, salesTransactions } =
		useDashboardMetrics();
	const [viewMode, setViewMode] = useState<ViewMode>("dashboard");
	const [currentQuery, setCurrentQuery] = useState<string>("");
	const [conversationState, setConversationState] = useState<ConversationState>(
		createEmptyConversation(),
	);

	const overdueInvoices = useMemo((): OverdueInvoiceData[] | undefined => {
		if (mode !== "live") return undefined;
		const now = new Date();
		return salesTransactions
			.filter((txn) => {
				if ((txn.outstanding ?? 0) <= 0) return false;
				const dueDate = new Date(txn.dueDate ?? txn.transactionDate ?? "");
				return dueDate < now;
			})
			.map((txn) => ({
				customer: txn.accountName ?? "",
				description: txn.transactionDescription ?? "",
				dueDate: txn.dueDate ?? "",
				outstandingValue: txn.outstanding ?? 0,
			}));
	}, [mode, salesTransactions]);

	// Step 1 — filter by enabled, then permissions
	const allowedCards = MASTER_DASHBOARD_CARDS.filter((cardConfig) => {
		if (!cardConfig.enabled) return false;
		return hasPermission(cardConfig.permission as any);
	});

	// Step 1b — apply saved role layout order if one exists, otherwise use default
	const sortedCards = useMemo(() => {
		const layoutRoleKey = roleId?.toString() ?? currentUser.id;
		const savedLayout =
			mode === "live"
				? (liveLayoutData?.cards?.map((c) => ({
						cardId: c.cardId,
						order: c.order,
					})) ?? null)
				: loadLayoutForRole(layoutRoleKey);

		if (savedLayout && savedLayout.length > 0) {
			const savedOrderMap = new Map(
				savedLayout.map((s) => [s.cardId, s.order]),
			);
			const allowedIds = new Set(allowedCards.map((c) => c.id));

			// Place new cards at the end of their size tier
			const newCards = allowedCards.filter((c) => !savedOrderMap.has(c.id));
			for (const card of newCards) {
				const maxInTier = allowedCards
					.filter(
						(c) => c.minWidth === card.minWidth && savedOrderMap.has(c.id),
					)
					.reduce((max, c) => Math.max(max, savedOrderMap.get(c.id) ?? 0), 0);
				savedOrderMap.set(card.id, maxInTier + 10);
			}

			const sorted = [...allowedCards].sort((a, b) => {
				const orderA = savedOrderMap.get(a.id) ?? a.order + 10000;
				const orderB = savedOrderMap.get(b.id) ?? b.order + 10000;
				return orderA - orderB;
			});

			// Prune stale entries and persist if the layout changed
			const hasStaleEntries = savedLayout.some(
				(s) => !allowedIds.has(s.cardId),
			);
			if (mode === "demo" && (hasStaleEntries || newCards.length > 0)) {
				saveLayoutForRole(
					layoutRoleKey,
					sorted.map((c) => ({
						cardId: c.id,
						order: savedOrderMap.get(c.id) ?? c.order,
					})),
				);
			}

			return sorted;
		}
		return [...allowedCards].sort((a, b) => a.order - b.order);
	}, [allowedCards, mode, liveLayoutData, roleId, currentUser.id]);

	const totalCards = sortedCards.length;

	// Step 2 — group cards by tier (mini / medium / full) so each tier
	// renders as a single block regardless of saved-order interleaving.
	const TIER_ORDER: Array<"mini" | "medium" | "full"> = [
		"mini",
		"medium",
		"full",
	];
	const cardGroups = TIER_ORDER.map((tier) =>
		sortedCards.filter((c) => c.minWidth === tier),
	).filter((group) => group.length > 0);

	const handleSubmitQuery = (query: string) => {
		// Add user message to conversation
		setConversationState((prev) => addUserMessage(prev, query));
		setCurrentQuery(query);
		setViewMode("results");
	};

	const handleBackToDashboard = () => {
		setViewMode("dashboard");
		setCurrentQuery("");
		// Reset conversation when returning to dashboard
		setConversationState(resetConversation());
	};

	const handleSubmitFollowUp = (query: string) => {
		// Add follow-up to conversation history
		setConversationState((prev) => addUserMessage(prev, query));
		setCurrentQuery(query);
	};

	const handleUpdateResults = (results: any[], summary: string) => {
		// Update conversation with assistant response
		setConversationState((prev) => addAssistantMessage(prev, results, summary));
	};

	// Show loading state when permissions or metrics are not yet available
	if (permissionsLoading || (!metrics && isLoading)) {
		return (
			<div className="p-6 flex items-center justify-center min-h-[60vh]">
				<div className="text-muted-foreground">Loading dashboard...</div>
			</div>
		);
	}

	if (forecastNotReady) {
		return (
			<div className="p-6 min-w-0 overflow-x-hidden">
				<div className="flex flex-col gap-8">
					<div className="flex items-center gap-1.5">
						<h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
					</div>
					<Card className="border-dashed">
						<CardContent className="flex flex-col items-center justify-center py-16 text-center">
							<TrendingUp className="h-12 w-12 text-muted-foreground/40 mb-4" />
							<h3 className="text-lg font-semibold mb-2">
								No forecast data available
							</h3>
							<p className="text-muted-foreground text-sm max-w-md mb-6">
								A forecast refresh has not been run yet. Run a forecast refresh
								to populate your dashboard with cash position data, projections,
								and key financial metrics.
							</p>
							{hasPermission("trigger_forecast_refresh") && (
								<Button onClick={() => onNavigate?.("forecast-refresh")}>
									<RefreshCw className="h-4 w-4 mr-2" />
									Go to Refresh Forecast
								</Button>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	if (!metrics && error) {
		return (
			<div className="p-6 flex items-center justify-center min-h-[60vh]">
				<div className="text-center text-destructive">{error}</div>
			</div>
		);
	}

	// Show results view if in results mode
	if (viewMode === "results") {
		return (
			<QueryResultsView
				query={currentQuery}
				onBack={handleBackToDashboard}
				onSubmitFollowUp={handleSubmitFollowUp}
				conversationState={conversationState}
				onUpdateResults={handleUpdateResults}
			/>
		);
	}

	// Show dashboard view
	return (
		<div className="p-6 min-w-0 overflow-x-hidden">
			{/* Show welcome message if no dashboard cards are permitted */}
			{totalCards === 0 ? (
				<div className="flex items-center justify-center min-h-[60vh]">
					<div className="max-w-2xl text-center space-y-4">
						<h2 className="text-3xl font-semibold tracking-tight">
							Welcome to Cash Forecaster
						</h2>
						<p className="text-lg text-muted-foreground leading-relaxed">
							You access your content using the Navigation pane on the left. You
							can set your own personal preferences when using this app in My
							Preferences at the bottom of the Navigation pane. You can run
							product tours and access help from the question mark at the top
							right of your screen.
						</p>
					</div>
				</div>
			) : (
				/* Step 3 — Render each group in order */
				<div className="flex flex-col gap-8">
					{/* Page title */}
					<div className="flex items-center gap-1.5">
						<h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
						<InfoTooltip
							title="Cash Position"
							content="A real-time overview of BuildRight's cash position, forecast, and key financial metrics across all connected accounts."
						/>
					</div>
					{cardGroups.map((group, groupIndex) => {
						const minWidth = group[0].minWidth;

						if (minWidth === "full") {
							return (
								<div key={groupIndex} className="w-full flex flex-col gap-6">
									{group.map((cardConfig) => {
										const CardComponent = (Cards as any)[cardConfig.component];
										if (!CardComponent) {
											console.warn(
												`Card component not found: ${cardConfig.component}`,
											);
											return null;
										}
										return (
											<div key={cardConfig.id} className="w-full">
												<CardComponent
													onNavigate={onNavigate}
													metrics={metrics}
													overdueInvoices={overdueInvoices}
												/>
											</div>
										);
									})}
								</div>
							);
						}

						if (minWidth === "mini") {
							return (
								<div
									key={groupIndex}
									className="grid gap-6"
									style={{
										gridTemplateColumns: `repeat(${group.length}, minmax(0, 1fr))`,
									}}
								>
									{group.map((cardConfig) => {
										const CardComponent = (Cards as any)[cardConfig.component];
										if (!CardComponent) {
											console.warn(
												`Card component not found: ${cardConfig.component}`,
											);
											return null;
										}
										return (
											<div
												key={cardConfig.id}
												className="overflow-hidden [&>*]:!h-full min-w-0"
											>
												<CardComponent
													onNavigate={onNavigate}
													metrics={metrics}
													overdueInvoices={overdueInvoices}
												/>
											</div>
										);
									})}
								</div>
							);
						}

						if (minWidth === "medium") {
							return (
								<div
									key={groupIndex}
									className="grid grid-cols-2 gap-6 auto-rows-fr"
								>
									{group.map((cardConfig) => {
										const CardComponent = (Cards as any)[cardConfig.component];
										if (!CardComponent) {
											console.warn(
												`Card component not found: ${cardConfig.component}`,
											);
											return null;
										}
										return (
											<div
												key={cardConfig.id}
												className="min-h-[300px] flex flex-col [&>*]:flex-1"
											>
												<CardComponent
													onNavigate={onNavigate}
													metrics={metrics}
													overdueInvoices={overdueInvoices}
												/>
											</div>
										);
									})}
								</div>
							);
						}

						return null;
					})}
				</div>
			)}
		</div>
	);
}
